var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable import/no-cycle */
import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import withSDK from '../../../core/hocs/withSDK';
import { useConfig } from '../../../social/providers/ConfigProvider';
import { confirm } from '../../../core/components/Confirm';
import customizableComponent from '../../../core/hocs/customization';
import useComment from '../../../social/hooks/useComment';
import useUser from '../../../core/hooks/useUser';
import CommentComposeBar from '../../../social/components/CommentComposeBar';
import CommentList from '../../../social/components/CommentList';
import { notification } from '../../../core/components/Notification';
import { canPerformStingActions, canPerformUserLookups, isModerator } from '../../../helpers/permissions';
import StyledComment from './Comment.styles';
import useSocialMention from '../../../social/hooks/useSocialMention';
import usePost from '../../../social/hooks/usePost';
import { useNavigation } from '../../../social/providers/NavigationProvider';
import { getUserType, shouldHighlightUserType } from '../../../helpers/userTypes';
import { CommentBlock, CommentContainer, ReplyContainer, DeletedCommentContainer, DeletedReplyContainer, DeletedIcon, Text, IconContainer, MessageContainer, } from './styles';
import { extractMetadata, parseMentionsMarkup } from '../../../helpers/utils';
const REPLIES_PER_PAGE = 5;
const DeletedComment = () => {
    return (React.createElement(DeletedCommentContainer, { "data-qa-anchor": "comment-deleted-comment" },
        React.createElement(IconContainer, null,
            React.createElement(DeletedIcon, null)),
        React.createElement(MessageContainer, null,
            React.createElement(Text, { "data-qa-anchor": "comment-deleted-comment-text" },
                React.createElement(FormattedMessage, { id: "comment.deleted" })))));
};
const DeletedReply = () => {
    return (React.createElement("div", null,
        React.createElement(DeletedReplyContainer, { "data-qa-anchor": "reply-deleted-reply" },
            React.createElement(IconContainer, { className: "reply" },
                React.createElement(DeletedIcon, null)),
            React.createElement(MessageContainer, null,
                React.createElement(Text, { "data-qa-anchor": "reply-deleted-reply-text" },
                    React.createElement(FormattedMessage, { id: "reply.deleted" }))))));
};
const Comment = ({ readonly = false, commentId, currentUserId, userRoles, handleCopyPath, isCommentingEnabled, }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const [isReplying, setIsReplying] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const { formatMessage } = useIntl();
    const [isExpanded, setExpanded] = useState(false);
    const { onClickUser } = useNavigation();
    const { showOldStyleComments: isOldStyle, manateeUrl, dashboardUrl } = useConfig();
    const { isCommentReady, comment, commentAuthor, commentAuthorAvatar, handleReportComment, handleReplyToComment, handleEditComment, handleDeleteComment, isFlaggedByMe, } = useComment({ commentId });
    const { comment: parentComment, handleReplyToComment: handleReplyToParentComment, } = useComment({ commentId: comment.parentId });
    const { post } = usePost(comment === null || comment === void 0 ? void 0 : comment.referenceId);
    const { text: localText, markup, mentions, onChange, queryMentionees, resetState, clearAll, } = useSocialMention({
        targetId: post === null || post === void 0 ? void 0 : post.targetId,
        targetType: post === null || post === void 0 ? void 0 : post.targetType,
        remoteText: (_b = (_a = comment === null || comment === void 0 ? void 0 : comment.data) === null || _a === void 0 ? void 0 : _a.text) !== null && _b !== void 0 ? _b : '',
        remoteMarkup: parseMentionsMarkup((_c = comment === null || comment === void 0 ? void 0 : comment.data) === null || _c === void 0 ? void 0 : _c.text, comment === null || comment === void 0 ? void 0 : comment.metadata),
    });
    const onReportClick = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield handleReportComment();
            if (isFlaggedByMe) {
                notification.success({
                    content: formatMessage({ id: 'report.unreportSent' }),
                });
            }
            else {
                notification.success({
                    content: formatMessage({ id: 'report.reportSent' }),
                });
            }
        }
        catch (err) {
            notification.error({
                content: err.message,
            });
        }
    });
    const handleClickUser = () => onClickUser(commentAuthor.userId);
    const onCopyPathClick = () => {
        handleCopyPath(comment);
    };
    const [text, setText] = useState((_e = (_d = comment === null || comment === void 0 ? void 0 : comment.data) === null || _d === void 0 ? void 0 : _d.text) !== null && _e !== void 0 ? _e : '');
    useEffect(() => {
        var _a, _b;
        if (text !== ((_a = comment === null || comment === void 0 ? void 0 : comment.data) === null || _a === void 0 ? void 0 : _a.text)) {
            setText((_b = comment === null || comment === void 0 ? void 0 : comment.data) === null || _b === void 0 ? void 0 : _b.text);
        }
    }, [(_f = comment === null || comment === void 0 ? void 0 : comment.data) === null || _f === void 0 ? void 0 : _f.text, text]);
    const onClickReply = () => {
        setIsReplying((preValue) => !preValue);
    };
    const startEditing = () => {
        setIsEditing(true);
    };
    const cancelEditing = () => {
        setIsEditing(false);
        resetState();
    };
    const handleEdit = () => {
        const { metadata, mentionees } = extractMetadata(mentions);
        handleEditComment(localText, mentionees, metadata);
        clearAll();
        setIsEditing(false);
    };
    const isCommentOwner = commentAuthor.userId === currentUserId;
    const isReplyComment = !!comment.parentId;
    const userType = getUserType(commentAuthor);
    const isHighlighted = shouldHighlightUserType(userType);
    const deleteComment = () => {
        const title = isReplyComment ? 'reply.delete' : 'comment.delete';
        const content = isReplyComment ? 'reply.deleteBody' : 'comment.deleteBody';
        confirm({
            'data-qa-anchor': 'delete-comment',
            title: React.createElement(FormattedMessage, { id: title }),
            content: React.createElement(FormattedMessage, { id: content }),
            cancelText: formatMessage({ id: 'comment.deleteConfirmCancelText' }),
            okText: formatMessage({ id: 'comment.deleteConfirmOkText' }),
            onOk: handleDeleteComment,
        });
    };
    const onLookupUser = () => {
        window.open(`${manateeUrl}/user/${commentAuthor.userId}`, '_blank');
    };
    const onOpenUserDashboard = () => {
        window.open(`${dashboardUrl}/inbox/${currentUserId}/coachee/${commentAuthor.userId}`, '_blank');
    };
    const { user: currentUser } = useUser(currentUserId, [currentUserId]);
    const currentUserType = getUserType(currentUser);
    const canDelete = (!readonly && isCommentOwner) || isModerator(userRoles);
    const canEdit = !readonly && isCommentOwner;
    const canLike = !readonly;
    const canReply = !readonly && isCommentingEnabled;
    const canReport = !readonly && !isCommentOwner;
    const canLookup = canPerformUserLookups({ actingUserType: currentUserType, targetUserType: userType });
    const canSting = canPerformStingActions({ actingUserType: currentUserType, targetUserType: userType });
    if (!isCommentReady) {
        return null;
    }
    if (comment.isDeleted) {
        return isReplyComment ? (React.createElement(DeletedReply, null)) : (React.createElement(CommentBlock, null,
            React.createElement(DeletedComment, null)));
    }
    const renderedComment = (React.createElement(StyledComment, { commentId: comment.commentId, commentPath: comment.path, authorName: commentAuthor.displayName || commentAuthor.userId || formatMessage({ id: 'anonymous' }), authorId: commentAuthor.userId, authorType: isHighlighted ? userType : undefined, authorAvatar: commentAuthorAvatar.fileUrl, canDelete: canDelete, canEdit: canEdit, canLike: canLike, canReply: canReply, canReport: canReport, canLookup: canLookup, canSting: canSting, isBanned: commentAuthor.isGlobalBan, createdAt: comment.createdAt, editedAt: comment.editedAt, mentionees: (_g = comment === null || comment === void 0 ? void 0 : comment.metadata) === null || _g === void 0 ? void 0 : _g.mentioned, metadata: comment === null || comment === void 0 ? void 0 : comment.metadata, text: text, markup: markup, handleReportComment: onReportClick, startEditing: startEditing, cancelEditing: cancelEditing, handleEdit: handleEdit, handleDelete: deleteComment, handleLookup: onLookupUser, handleOpenDash: onOpenUserDashboard, isEditing: isEditing, queryMentionees: queryMentionees, isReported: isFlaggedByMe, isReplyComment: isReplyComment, onClickReply: onClickReply, onClickUser: handleClickUser, onChange: onChange, handleCopyPath: handleCopyPath ? onCopyPathClick : undefined, isOldStyle: isOldStyle, displayAccessCode: currentUserType === "coach" }));
    const onSubmitReply = (replyText, mentionees, metadata) => {
        handleReplyToParentComment(replyText, mentionees, metadata, !isCommentingEnabled);
        setIsReplying(false);
        setExpanded(true);
    };
    const onSubmitComment = (replyText, mentionees, metadata) => {
        handleReplyToComment(replyText, mentionees, metadata, !isCommentingEnabled);
        setIsReplying(false);
        setExpanded(true);
    };
    return isReplyComment ? (React.createElement(React.Fragment, null,
        isReplying && (React.createElement(CommentComposeBar, { postId: parentComment === null || parentComment === void 0 ? void 0 : parentComment.referenceId, postType: parentComment === null || parentComment === void 0 ? void 0 : parentComment.referenceType, userToReply: commentAuthor.displayName, onSubmit: onSubmitReply, onCancel: onClickReply })),
        React.createElement(ReplyContainer, { "data-qa-anchor": "reply" }, renderedComment))) : (React.createElement(CommentBlock, null,
        React.createElement(CommentContainer, { "data-comment-id": comment.commentId, "data-qa-anchor": "comment" }, renderedComment),
        isReplying && (React.createElement(CommentComposeBar, { postId: comment === null || comment === void 0 ? void 0 : comment.referenceId, postType: comment === null || comment === void 0 ? void 0 : comment.referenceType, userToReply: commentAuthor.displayName, onSubmit: onSubmitComment, onCancel: onClickReply })),
        React.createElement(CommentList, { parentId: commentId, referenceId: comment.referenceId, last: REPLIES_PER_PAGE, readonly: readonly, isExpanded: isExpanded })));
};
Comment.propTypes = {
    readonly: PropTypes.bool,
    commentId: PropTypes.string.isRequired,
    currentUserId: PropTypes.string.isRequired,
    userRoles: PropTypes.array,
    handleCopyPath: PropTypes.func,
    isCommentingEnabled: PropTypes.bool,
};
export default memo(withSDK(customizableComponent('Comment', Comment)));
//# sourceMappingURL=index.js.map